import { z } from 'zod';
import { createPortalToolOutputSchema } from './portal-tool.schemas';
import { MATCH_REPLACE_RULE_TOOL_NAME } from './portal-tool.constants';
import {
  collectMatchReplaceRuleInputIssues,
  normalizeMatchReplaceRuleInput,
} from './match-replace-rule.validation';

export {
  collectMatchReplaceRuleInputIssues,
  isValidMatchReplaceRuleInput,
  normalizeMatchReplaceRuleInput,
} from './match-replace-rule.validation';
export type { MatchReplaceRuleInputLike } from './match-replace-rule.validation';

export { MATCH_REPLACE_RULE_TOOL_NAME };

export const MatchReplaceActionSchema = z.enum([
  'list',
  'add',
  'update',
  'remove',
  'toggle-engine',
]);

export const MatchReplaceMatchTypeSchema = z.enum([
  'url',
  'header',
  'body',
  'statusCode',
  'method',
  'addHeader',
  'removeHeader',
  'paramName',
  'paramValue',
  'firstLine',
]);

export const MatchReplaceMatchLocationSchema = z.enum([
  'request',
  'response',
  'both',
]);

export const MatchReplaceOriginSchema = z.enum(['proxy', 'coder', 'voyager']);

const MatchReplaceRuleInputObjectSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  enabled: z.boolean().optional(),
  description: z.string().optional(),
  matchType: MatchReplaceMatchTypeSchema,
  matchLocation: MatchReplaceMatchLocationSchema,
  matchPattern: z.string(),
  isRegex: z.boolean().optional(),
  caseSensitive: z.boolean().optional(),
  headerName: z.string().optional(),
  replaceWith: z.string(),
  replaceType: z.enum(['string', 'regex', 'function']).optional(),
  applyTo: MatchReplaceOriginSchema.array().nonempty().optional(),
  conditions: z
    .object({
      urlPattern: z.string().optional(),
      methods: z.array(z.string()).optional(),
      statusCodes: z.array(z.number()).optional(),
      contentTypes: z.array(z.string()).optional(),
    })
    .optional(),
});

export const MatchReplaceRuleInputSchema =
  MatchReplaceRuleInputObjectSchema.transform(
    normalizeMatchReplaceRuleInput,
  ).superRefine((data, ctx) => {
    for (const issue of collectMatchReplaceRuleInputIssues(data)) {
      ctx.addIssue({
        code: 'custom',
        message: issue,
      });
    }
  });

export const MatchReplaceRuleToolInputSchema = z
  .object({
    action: MatchReplaceActionSchema,
    ruleId: z.string().optional(),
    rule: MatchReplaceRuleInputSchema.optional(),
    nameFilter: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if ((data.action === 'add' || data.action === 'update') && !data.rule) {
      ctx.addIssue({
        code: 'custom',
        message: 'rule is required for add and update actions',
        path: ['rule'],
      });
    }

    if (data.action === 'update' && !data.ruleId?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'ruleId is required for update action',
        path: ['ruleId'],
      });
    }

    if (data.action === 'remove' && !data.ruleId?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'ruleId is required for remove action',
        path: ['ruleId'],
      });
    }
  });

export const MatchReplaceRuleSummarySchema = z.object({
  ruleId: z.string(),
  ruleName: z.string(),
  enabled: z.boolean(),
  matchType: MatchReplaceMatchTypeSchema,
  matchLocation: MatchReplaceMatchLocationSchema,
  matchPattern: z.string(),
  replaceWith: z.string(),
  isRegex: z.boolean().optional(),
  headerName: z.string().optional(),
  applyTo: MatchReplaceOriginSchema.array().optional(),
});

export const MatchReplaceRuleDataSchema = z.object({
  ruleId: z.string().optional(),
  ruleName: z.string().optional(),
  engineEnabled: z.boolean(),
  ruleCount: z.number(),
  rules: z.array(MatchReplaceRuleSummarySchema).optional(),
});

export const MatchReplaceRuleOutputSchema = createPortalToolOutputSchema({
  dataSchema: MatchReplaceRuleDataSchema,
});

export type MatchReplaceRuleToolInput = z.infer<
  typeof MatchReplaceRuleToolInputSchema
>;
export type MatchReplaceRuleSummary = z.infer<
  typeof MatchReplaceRuleSummarySchema
>;
export type MatchReplaceRuleOutput = z.infer<
  typeof MatchReplaceRuleOutputSchema
>;
export type MatchReplaceRuleData = z.infer<typeof MatchReplaceRuleDataSchema>;
